import { useState, useEffect, useRef } from 'react';
import { ArrowRight, Check, ChevronDown, ArrowDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { fbqTrack } from '@/lib/metaPixel';

const FOUNDER_IMG = "https://customer-assets.emergentagent.com/job_55d45055-acc3-40b2-9f65-62def28cea27/artifacts/loq3nda5_self%20first%204.jpeg";
const API_URL = process.env.REACT_APP_BACKEND_URL;

const SIGNS = [
  "Why you can be successful and still feel off",
  "What high-achieving women are carrying that no one sees",
  "How over-responsibility quietly drains your energy",
  "Why performance alone isn't moving you forward",
  "The hidden cost of staying too long in the wrong season",
  "How to recognize when you're in transition (even if you can't name it yet)",
  "Why you don't need to push harder — you need something different",
];

const DiamondResetPage = () => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showSticky, setShowSticky] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    // Meta Pixel `init` + `PageView` is fired site-wide from public/index.html.
    // We don't init here to avoid the duplicate-pixel warning in Meta Pixel Helper.
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (submitted) { setShowSticky(false); return; }
      const formEl = formRef.current;
      if (!formEl) { setShowSticky(window.scrollY > 600); return; }
      const rect = formEl.getBoundingClientRect();
      setShowSticky(rect.top > window.innerHeight || rect.bottom < 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [submitted]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await fetch(`${API_URL}/api/funnel/opt-in`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), first_name: firstName.trim() }),
      });
      // Fire Meta Pixel Lead event on successful opt-in
      fbqTrack('Lead', { content_name: 'Diamond Reset Guide' });
      // Always show success - the email is sent regardless of response status
      setSubmitted(true);
      toast.success('Your guide is on its way! Check your inbox.');
      // Redirect to Reset Room after a short delay so the toast is visible
      setTimeout(() => { window.location.href = '/reset-room'; }, 1500);
    } catch {
      // Even on network error, show success since the request likely went through
      fbqTrack('Lead', { content_name: 'Diamond Reset Guide' });
      setSubmitted(true);
      toast.success('Please check your email, the guide has been sent.');
      setTimeout(() => { window.location.href = '/reset-room'; }, 1500);
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('opt-in-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div style={{ fontFamily: "'Manrope', sans-serif", color: '#1A3C34', background: '#F9F8F6' }}>

      {/* ── HERO ── */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden" style={{ background: 'linear-gradient(135deg, #1A3C34 0%, #2C5E52 100%)' }}>
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-80 h-80 rounded-full blur-3xl" style={{ backgroundColor: '#C06C54' }} />
          <div className="absolute bottom-10 right-20 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: '#E8DCCA' }} />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 relative z-10 w-full">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Text */}
            <div className="text-center lg:text-left">
              <p className="text-sm tracking-[4px] uppercase mb-6" style={{ color: '#E8DCCA' }}>
                A Free Guide for High-Achieving Women
              </p>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                What Is Your Life<br />Actually Costing You?
              </h1>
              <p className="text-xl max-w-xl mx-auto lg:mx-0 mb-4 leading-relaxed" style={{ color: '#E8DCCA' }}>
                A short guide for high-achieving women who are carrying too much &mdash; and starting to feel it.
              </p>
              <div className="w-12 h-px mx-auto lg:mx-0 my-8" style={{ backgroundColor: '#C06C54' }} />
              <p className="max-w-md mx-auto lg:mx-0 leading-relaxed italic text-white/80" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '22px' }}>
                You can be successful&hellip;<br />
                and still feel overwhelmed, unsupported, and disconnected.<br />
                This guide will help you understand why.
              </p>
              <button
                onClick={scrollToForm}
                className="mt-10 inline-flex items-center gap-2 px-8 py-4 text-white rounded-full text-base tracking-wider uppercase transition-all duration-300 hover:opacity-90 hover:-translate-y-0.5 shadow-lg"
                style={{ backgroundColor: '#C06C54' }}
                data-testid="hero-cta-btn"
              >
                Download the Free Guide <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Right: Founder Image */}
            <div className="hidden lg:flex justify-center">
              <div className="relative">
                <div className="absolute -inset-4 rounded-2xl opacity-20 blur-xl" style={{ backgroundColor: '#C06C54' }} />
                <img
                  src={FOUNDER_IMG}
                  alt="Crystal Khalil — Sister Diamonds Founder"
                  className="relative rounded-2xl shadow-2xl w-full max-w-md object-cover"
                  style={{ aspectRatio: '4/5' }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-5 h-5 text-white/30" />
        </div>
      </section>

      {/* ── MIRROR HER REALITY ── */}
      <section className="py-20 px-6" style={{ backgroundColor: '#F9F8F6' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-10" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A3C34' }}>
            You&rsquo;re the one people depend on.
          </h2>

          {/* Image Collage */}
          <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-12 max-w-3xl mx-auto">
            <div className="rounded-xl overflow-hidden shadow-md aspect-[3/4]">
              <img
                src="https://customer-assets.emergentagent.com/job_55d45055-acc3-40b2-9f65-62def28cea27/artifacts/5dzc7p6s_leading%20team.jpeg"
                alt="Leading the team"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="rounded-xl overflow-hidden shadow-md aspect-[3/4] mt-6">
              <img
                src="https://customer-assets.emergentagent.com/job_55d45055-acc3-40b2-9f65-62def28cea27/artifacts/1d41j8zt_Mother%20w%20kids3.jpeg"
                alt="Mother with children"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="rounded-xl overflow-hidden shadow-md aspect-[3/4]">
              <img
                src="https://customer-assets.emergentagent.com/job_55d45055-acc3-40b2-9f65-62def28cea27/artifacts/c8ytgh0b_caring%20for%20mom.jpeg"
                alt="Caring for family"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="max-w-2xl mx-auto">
            <div className="space-y-4 text-center text-xl leading-relaxed" style={{ color: '#4A6D62' }}>
              <p>You handle it. You show up. You figure it out.</p>
              <p>And from the outside, everything looks like it&rsquo;s working.</p>
              <p className="font-semibold pt-4" style={{ color: '#1A3C34' }}>But internally?<br />Something feels off.</p>
            </div>
            <div className="mt-10 space-y-4 max-w-lg mx-auto">
              <p className="text-sm uppercase tracking-widest mb-2 font-semibold" style={{ color: '#C06C54' }}>You may be:</p>
            {[
              "Tired in a way that rest doesn\u2019t fix",
              "Unsure what\u2019s next \u2014 even though you\u2019ve done so much",
              "Carrying more than your share in your relationships or work",
              "Feeling unseen, underleveraged, or stuck",
              "Wondering why success doesn\u2019t feel like freedom",
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3" style={{ color: '#1A3C34' }}>
                <div className="w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5" style={{ borderColor: '#C06C54' }}>
                  <Check className="w-3 h-3" style={{ color: '#C06C54' }} />
                </div>
                <p className="text-lg leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
          </div>
        </div>
      </section>

      {/* ── REFRAME ── */}
      <section className="py-16 px-6" style={{ backgroundColor: '#1A3C34' }}>
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            This is not a discipline problem.
          </h2>
          <p className="text-xl mb-8" style={{ color: '#E8DCCA' }}>This is not about doing more. This is about what happens when:</p>
          <div className="space-y-4 text-left max-w-lg mx-auto">
            <div className="flex items-start gap-3">
              <span className="text-xl mt-0.5" style={{ color: '#C06C54' }}>&rarr;</span>
              <p className="text-white text-xl">You&rsquo;ve outgrown your current environment</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-xl mt-0.5" style={{ color: '#C06C54' }}>&rarr;</span>
              <p className="text-white text-xl">But you haven&rsquo;t fully stepped into what&rsquo;s next</p>
            </div>
          </div>
          <div className="w-12 h-px mx-auto my-10" style={{ backgroundColor: '#C06C54' }} />
          <p className="text-xl italic" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '24px', color: '#E8DCCA' }}>
            And that space in between?<br />That&rsquo;s where the weight shows up.
          </p>
        </div>
      </section>

      {/* ── WHAT THEY GET: 7 SIGNS ── */}
      <section className="py-20 px-6" style={{ backgroundColor: '#F9F8F6' }}>
        <div className="max-w-2xl mx-auto">
          <p className="text-sm uppercase tracking-widest text-center mb-3" style={{ color: '#C06C54' }}>Inside this free guide</p>
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A3C34' }}>
            7 Signs You May Have Outgrown<br />Your Current Environment
          </h2>
          <div className="space-y-4">
            {SIGNS.map((sign, i) => (
              <div key={i} className="flex items-start gap-4 p-4 rounded-xl bg-white border hover:shadow-md transition-all duration-300" style={{ borderColor: '#E8DCCA' }}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: '#C06C54', opacity: 0.15 }}>
                  <span className="text-sm font-medium" style={{ color: '#C06C54' }}>{i + 1}</span>
                </div>
                <p className="text-lg leading-relaxed" style={{ color: '#1A3C34' }}>{sign}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── EMOTIONAL CLOSE ── */}
      <section className="py-16 px-6" style={{ backgroundColor: '#E8DCCA' }}>
        <div className="max-w-xl mx-auto text-center">
          <p className="text-xl mb-6 leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '24px', color: '#4A6D62' }}>
            If you&rsquo;ve been feeling like:
          </p>
          <blockquote className="text-3xl sm:text-4xl font-bold italic leading-snug mb-8" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A3C34' }}>
            &ldquo;Something has to change&hellip;<br />but I don&rsquo;t know exactly what yet.&rdquo;
          </blockquote>
          <p className="text-xl leading-relaxed" style={{ color: '#4A6D62' }}>
            This guide will help you see what&rsquo;s really happening.
          </p>
        </div>
      </section>

      {/* ── OPT-IN FORM ── */}
      <section id="opt-in-form" ref={formRef} className="py-20 px-6" style={{ backgroundColor: '#1A3C34' }}>
        <div className="max-w-md mx-auto text-center">
          {submitted ? (
            <div className="py-12">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: 'rgba(192,108,84,0.2)' }}>
                <Check className="w-8 h-8" style={{ color: '#C06C54' }} />
              </div>
              <h3 className="text-2xl text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Please check your email.
              </h3>
              <p className="text-sm" style={{ color: '#E8DCCA' }}>The guide has been sent. Pay attention to what resonates.</p>
              <a
                href="/go-deeper"
                className="inline-flex items-center gap-2 mt-8 text-sm hover:underline"
                style={{ color: '#C06C54' }}
                data-testid="go-deeper-link"
              >
                Ready to go deeper? <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          ) : (
            <>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Get the Guide
              </h2>
              <p className="text-base mb-8" style={{ color: '#E8DCCA' }}>
                Because no matter how capable you are&hellip;<br />
                You can&rsquo;t carry everything and still feel free.
              </p>
              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  type="text"
                  placeholder="First name (optional)"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-5 py-3.5 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none transition-colors"
                  style={{ borderColor: 'rgba(255,255,255,0.2)' }}
                  data-testid="funnel-first-name"
                />
                <input
                  type="email"
                  placeholder="Your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-5 py-3.5 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none transition-colors"
                  style={{ borderColor: 'rgba(255,255,255,0.2)' }}
                  data-testid="funnel-email-input"
                />
                <button
                  type="submit"
                  disabled={submitting || !email.trim()}
                  className="w-full px-8 py-3.5 text-white rounded-full text-sm tracking-wider uppercase transition-all duration-300 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#C06C54' }}
                  data-testid="opt-in-submit"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Download the Free Guide'}
                  {!submitting && <ArrowRight className="w-4 h-4" />}
                </button>
              </form>
              <p className="text-xs mt-4" style={{ color: 'rgba(232,220,202,0.4)' }}>
                We respect your privacy. Unsubscribe anytime.
              </p>
            </>
          )}
        </div>
      </section>

      {/* ── FINAL TRUTH ── */}
      <section className="py-16 px-6" style={{ backgroundColor: '#F9F8F6' }}>
        <div className="max-w-lg mx-auto text-center">
          <div className="w-8 h-px mx-auto mb-8" style={{ backgroundColor: '#C06C54' }} />
          <p className="text-2xl sm:text-3xl font-bold leading-relaxed italic" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A3C34' }}>
            Because no matter how capable you are&hellip;<br />
            You can&rsquo;t carry everything and still feel free.
          </p>
          <div className="w-8 h-px mx-auto mt-8" style={{ backgroundColor: '#C06C54' }} />
        </div>
      </section>

      {/* ── STICKY MOBILE CTA BAR ── */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 transition-transform duration-300 ${showSticky ? 'translate-y-0' : 'translate-y-full'}`}
        data-testid="sticky-cta-bar"
      >
        <div className="backdrop-blur-md border-t px-4 py-3 flex items-center justify-between gap-3 max-w-xl mx-auto sm:max-w-none"
          style={{ backgroundColor: 'rgba(26,60,52,0.95)', borderColor: 'rgba(192,108,84,0.3)' }}
        >
          <p className="text-white text-sm font-medium hidden sm:block">
            Ready to put yourself first?
          </p>
          <button
            onClick={scrollToForm}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 text-white font-semibold px-6 py-2.5 rounded-full text-sm transition-all duration-300 hover:opacity-90 shadow-lg"
            style={{ backgroundColor: '#C06C54' }}
            data-testid="sticky-cta-btn"
          >
            Download the Free Guide
            <ArrowDown className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DiamondResetPage;
